import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { MessageCircle, X, Send, Sparkles, UserRound, Check } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { Reveal, Kicker } from "@/components/motion";
import { api } from "@/lib/api";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let sid = sessionStorage.getItem("nova-assistant-session");
  if (!sid) {
    sid = `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem("nova-assistant-session", sid);
  }
  return sid;
};

const md = {
  p: (props) => <p className="mb-2 last:mb-0" {...props} />,
  ul: (props) => <ul className="mb-2 list-disc space-y-1 pl-4 last:mb-0" {...props} />,
  ol: (props) => <ol className="mb-2 list-decimal space-y-1 pl-4 last:mb-0" {...props} />,
  li: (props) => <li {...props} />,
  strong: (props) => <strong className="font-semibold" {...props} />,
};

const Bubble = ({ role, children, testid }) => (
  <div className={`flex ${role === "user" ? "justify-end" : "justify-start"}`}>
    <div
      data-testid={testid}
      className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
        role === "user" ? "rounded-br-md bg-[#2E8BFF] text-white" : "rounded-bl-md bg-[#F8FAFC] text-[#111827]"
      }`}
    >
      {children}
    </div>
  </div>
);

export const AssistantChat = () => {
  const { pathname } = useLocation();
  // Tunnels de conversion : la bulle ne doit jamais recouvrir un champ de formulaire sur mobile
  const onFocusFlow = pathname === "/accompagnement" || pathname.startsWith("/lifematch/test");
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showLead, setShowLead] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", phone: "" });
  const [leadState, setLeadState] = useState("idle"); // idle | error | sent
  const scrollRef = useRef(null);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("nova-assistant-open", handler);
    return () => window.removeEventListener("nova-assistant-open", handler);
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy, showLead]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }]);
    try {
      const res = await fetch(`${BASE}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let started = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          let data;
          try { data = JSON.parse(part.slice(6)); } catch { continue; }
          if (data.delta) {
            if (!started) {
              started = true;
              setMessages((m) => [...m, { role: "assistant", content: data.delta }]);
            } else {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + data.delta };
                return copy;
              });
            }
          } else if (data.error) {
            setMessages((m) => [...m, { role: "assistant", content: t("assistant.error") }]);
          }
        }
      }
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: t("assistant.error") }]);
    }
    setBusy(false);
  }, [input, busy, t]);

  const submitLead = async () => {
    if (lead.name.trim().length < 2 || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(lead.email)) {
      setLeadState("error");
      return;
    }
    const summary = messages.slice(-8).map((m) => `${m.role}: ${m.content}`).join("\n").slice(0, 1500);
    try {
      await api.post("/leads", { type: "assistant", lang: i18n.language?.startsWith("en") ? "en" : "fr", ...lead, conversation_summary: summary });
      setLeadState("sent");
      setTimeout(() => { setShowLead(false); setLeadState("idle"); setLead({ name: "", email: "", phone: "" }); }, 2600);
    } catch {
      setLeadState("error");
    }
  };

  return (
    <>
      {/* Floating button — masqué sur mobile dans les tunnels de conversion
          (/accompagnement, /lifematch/test) pour ne jamais recouvrir les champs du formulaire */}
      <button
        data-testid="assistant-fab"
        onClick={() => setOpen((o) => !o)}
        aria-label="Assistant NovaImmo"
        className={`${onFocusFlow ? "hidden sm:flex" : "flex"} fixed bottom-[72px] right-4 z-[65] h-14 w-14 items-center justify-center rounded-full bg-[#2E8BFF] text-white shadow-[0_10px_32px_rgba(46,139,255,0.45)] transition-transform duration-300 hover:scale-110`}
      >
        <span className="pulse-ring absolute inset-0 rounded-full border-2 border-[#4DB8FF]/50" />
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
      </button>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className={`${onFocusFlow ? "hidden sm:flex" : "flex"} fixed bottom-[136px] right-4 z-[65] h-[min(580px,68vh)] w-[min(400px,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border border-[#111827]/10 bg-white shadow-[0_24px_80px_rgba(17,24,39,0.3)]`}
            data-testid="assistant-panel"
          >
            {/* Header */}
            <div className="flex items-center gap-3 bg-gradient-to-r from-[#2E8BFF] to-[#4DB8FF] px-5 py-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10">
                <LogoMark className="h-6 w-6 text-white" />
              </span>
              <div className="flex-1">
                <p className="font-display text-base text-white">{t("assistant.title")}</p>
                <p className="flex items-center gap-1.5 text-[11px] text-[#4DB8FF]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#4DB8FF]" />
                  {t("assistant.subtitle")}
                </p>
              </div>
              <button data-testid="assistant-close" onClick={() => setOpen(false)} className="rounded-full p-1.5 text-white/60 transition-colors duration-300 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="assistant-messages">
              <Bubble role="assistant" testid="assistant-welcome">
                {t("assistant.welcome")}
              </Bubble>
              {messages.map((m, i) => (
                <Bubble key={i} role={m.role} testid={`assistant-msg-${m.role}-${i}`}>
                  {m.role === "assistant" ? <ReactMarkdown components={md}>{m.content}</ReactMarkdown> : m.content}
                </Bubble>
              ))}
              {busy && messages[messages.length - 1]?.role === "user" && (
                <div className="flex justify-start" data-testid="assistant-typing">
                  <div className="flex items-center gap-1.5 rounded-2xl rounded-bl-md bg-[#F8FAFC] px-4 py-3">
                    {[0, 1, 2].map((d) => (
                      <motion.span
                        key={d}
                        className="h-1.5 w-1.5 rounded-full bg-[#111827]/40"
                        animate={{ opacity: [0.3, 1, 0.3] }}
                        transition={{ repeat: Infinity, duration: 1.1, delay: d * 0.18 }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Inline lead form */}
              <AnimatePresence>
                {showLead && (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 12 }}
                    className="rounded-2xl border border-[#2E8BFF]/25 bg-[#2E8BFF]/5 p-4"
                    data-testid="assistant-lead-form"
                  >
                    {leadState === "sent" ? (
                      <div className="flex items-center gap-2.5 py-2 text-sm font-medium text-[#2E8BFF]" data-testid="assistant-lead-success">
                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#2E8BFF] text-white"><Check className="h-4 w-4" /></span>
                        {t("assistant.leadSuccess")}
                      </div>
                    ) : (
                      <>
                        <p className="text-sm font-semibold text-[#111827]">{t("assistant.leadTitle")}</p>
                        <p className="mt-1 text-xs text-slate-500">{t("assistant.leadDesc")}</p>
                        <div className="mt-3 space-y-2">
                          <input data-testid="assistant-lead-name" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} placeholder={t("terrain.fields.name")} className="w-full rounded-lg border border-[#111827]/15 bg-white px-3 py-2 text-sm outline-none transition-colors duration-300 focus:border-[#2E8BFF]" />
                          <input data-testid="assistant-lead-email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} placeholder={t("terrain.fields.email")} type="email" className="w-full rounded-lg border border-[#111827]/15 bg-white px-3 py-2 text-sm outline-none transition-colors duration-300 focus:border-[#2E8BFF]" />
                          <input data-testid="assistant-lead-phone" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} placeholder={t("terrain.fields.phone")} className="w-full rounded-lg border border-[#111827]/15 bg-white px-3 py-2 text-sm outline-none transition-colors duration-300 focus:border-[#2E8BFF]" />
                        </div>
                        {leadState === "error" && <p className="mt-2 text-xs text-red-600" data-testid="assistant-lead-error">{t("terrain.errors.email")}</p>}
                        <button data-testid="assistant-lead-submit" onClick={submitLead} className="mt-3 w-full rounded-xl bg-[#2E8BFF] py-2.5 text-sm font-semibold text-white transition-transform duration-300 hover:scale-[1.02]">
                          {t("assistant.leadSubmit")}
                        </button>
                      </>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Footer */}
            <div className="border-t border-[#111827]/8 px-4 pb-4 pt-3">
              <button
                data-testid="assistant-lead-btn"
                onClick={() => setShowLead((s) => !s)}
                className="mb-2.5 inline-flex items-center gap-1.5 rounded-full border border-[#2E8BFF]/30 bg-[#2E8BFF]/5 px-3.5 py-1.5 text-xs font-semibold text-[#2E8BFF] transition-colors duration-300 hover:bg-[#2E8BFF]/10"
              >
                <UserRound className="h-3.5 w-3.5" />
                {t("assistant.leadBtn")}
              </button>
              <div className="flex items-center gap-2">
                <input
                  data-testid="assistant-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
                  placeholder={t("assistant.placeholder")}
                  className="flex-1 rounded-full border border-[#111827]/15 bg-[#F8FAFC] px-4 py-2.5 text-sm outline-none transition-colors duration-300 focus:border-[#2E8BFF]"
                />
                <button
                  data-testid="assistant-send-btn"
                  onClick={send}
                  disabled={busy || !input.trim()}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#2E8BFF] text-white transition-transform duration-300 hover:scale-110 disabled:opacity-40"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
              <p className="mt-2.5 text-center text-[10px] leading-snug text-slate-400" data-testid="assistant-ai-disclaimer">
                {t("assistant.disclaimer")}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Home page entry point banner
export const AssistantHomeBanner = () => {
  const { t } = useTranslation();
  return (
    <section className="border-t border-[#111827]/5 bg-[#F8FAFC] py-20" data-testid="assistant-home-banner">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <div className="flex flex-col items-start justify-between gap-8 rounded-2xl border border-[#2E8BFF]/20 bg-white p-9 md:flex-row md:items-center md:p-12">
            <div className="flex items-start gap-5">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#2E8BFF]/10 text-[#2E8BFF]">
                <Sparkles className="h-5 w-5" />
              </span>
              <div>
                <Kicker>{t("assistant.title")}</Kicker>
                <h2 className="mt-2 max-w-xl font-display text-2xl text-[#111827] md:text-3xl">{t("assistant.homeTitle")}</h2>
                <p className="mt-2 max-w-xl text-sm font-light leading-relaxed text-slate-600 md:text-base">{t("assistant.homeDesc")}</p>
              </div>
            </div>
            <button
              data-testid="assistant-home-cta"
              onClick={() => window.dispatchEvent(new CustomEvent("nova-assistant-open"))}
              className="inline-flex shrink-0 items-center gap-2 rounded-xl shadow-sm bg-[#2E8BFF] px-7 py-3.5 text-sm font-semibold text-white transition-transform duration-300 hover:scale-105"
            >
              <MessageCircle className="h-4 w-4" />
              {t("assistant.homeCta")}
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
